/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Elastic, Power2, Timeline, TimelineLite, TweenLite} from "gsap";
import {BackgroundAnimationsController} from "../../background/BackgroundAnimationsController";
import {ShakingAnimation} from "../../displayobject/ShakingAnimation";
import {WildNudgeGameConfig} from "../../WildNudgeGameConfig";
import {WildNudgeBigWinCountUp} from "./WildNudgeBigWinCountUp";

export class WildNudgeBigWinView extends BaseView {

    private static readonly FIRE_NAMES:string[] = ["M3_fire", "wild_fire", "red_fire"];

    private readonly _countUpPosition:[number, number] = [GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.55];

    private _layer:ScalableLayer;
    private _countUp:WildNudgeBigWinCountUp;
    private _bigWinText:ShakingAnimation;
    private _isShowing:boolean = false;
    private _dimmer:PIXI.Graphics;
    private _reelsHighlight:PIXI.Sprite;
    private _textGlow:PIXI.Sprite;
    private _fireContainer:PIXI.Container;
    private _fires:TimelineSprite[];

    constructor() {
        super();

        this._countUp = new WildNudgeBigWinCountUp();
        this._layer = StageManager.getLayer(WildNudgeGameConfig.instance.LAYERS.BIG_WIN.name);
    }

    protected initAnimations():void {
        this._dimmer = new PIXI.Graphics();
        this._dimmer.alpha = 0;
        this._layer.addChild(this._dimmer);

        this._reelsHighlight = new PIXI.Sprite(GameResources.getTextures("bigWinReelsLight")[0]);
        this._reelsHighlight.blendMode = PIXI.BLEND_MODES.ADD;
        this._reelsHighlight.anchor.set(0.5, 0.5);
        this._reelsHighlight.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        this._reelsHighlight.alpha = 0;
        this._layer.addChild(this._reelsHighlight);

        this._textGlow = new PIXI.Sprite(GameResources.getTextures("bigWinTextGlow")[0]);
        this._textGlow.blendMode = PIXI.BLEND_MODES.ADD;
        this._textGlow.anchor.set(0.5, 0.5);
        this._textGlow.scale.set(1, 0.5);
        this._textGlow.position.set(GameConfig.instance.GAME_WIDTH * 0.5, 230);
        this._textGlow.alpha = 0;
        this._layer.addChild(this._textGlow);

        this._bigWinText = new ShakingAnimation();
        this._bigWinText.anchor.set(0.5, 0.5);
        this._bigWinText.position.set(GameConfig.instance.GAME_WIDTH * 0.5, 230);
        this._bigWinText.alpha = 0;
        this._layer.addChild(this._bigWinText);

        this._fireContainer = new PIXI.Container();
        this._fireContainer.alpha = 0;
        this._fireContainer.position.set(this._countUpPosition[0], this._countUpPosition[1]);
        this._layer.addChild(this._fireContainer);

        const rect:PIXI.Graphics = new PIXI.Graphics();
        rect.beginFill(0xffffff);
        rect.drawRect(-311, -96, 622, 192);
        this._fireContainer.addChild(rect);

        this._fires = [];
        for(let i:number = 0; i < 6; i++) {
            const fire:TimelineSprite = new TimelineSprite(GameResources.getTextures(WildNudgeBigWinView.FIRE_NAMES[Math.floor(i / 2)]));
            fire.blendMode = PIXI.BLEND_MODES.ADD;
            fire.anchor.set(0.5, 0.67);
            fire.scale.set(2.6, 2.4);
            fire.rotation = Math.PI * 0.5 * Math.pow(-1, i + 1);
            fire.visible = false;
            this._fires.push(fire);
            this._fireContainer.addChild(fire);
        }

        this._countUp.position.set(this._countUpPosition[0], this._countUpPosition[1]);
        this._layer.addChild(this._countUp);
    }

    protected onResize(resizeData:IResizeData):void {
        if(!this._isShowing) {
            this._isResizeDirty = true;
            return;
        }

        this._isResizeDirty = false;

        const screenWidth:number = Math.ceil(resizeData.width / this._layer.scale.x);
        const screenHeight:number = Math.ceil(resizeData.height / this._layer.scale.y);
        const leftOffset:number = Math.floor(-this._layer.x / this._layer.scale.x);
        const topOffset:number = Math.floor(-this._layer.y / this._layer.scale.y);

        this._dimmer.clear();
        this._dimmer.beginFill(0x000000, 0);
        this._dimmer.drawRect(leftOffset, topOffset, screenWidth, screenHeight);
    }

    public getBigWinLevelAnimation(winLevel:number, winRatioConfig:IWinRatioConfig):Timeline {
        this._isShowing = true;
        if(this._isResizeDirty) {
            this.onResize(this._resizeData);
        }

        const timeline:TimelineLite = new TimelineLite({onComplete : () => this._bigWinText.stopShake()});
        EventHandler.dispatchEvent(new GameEvent(BackgroundAnimationsController.SET_FLASH_LIGHTS, {
            val : true,
            fps : 30 * (winLevel + 1)
        }));
        timeline.add([
            () => {
                this.updateBigWinLevelAnimation(winRatioConfig.text!);
                this._fires.forEach((fire:TimelineSprite, index:number) => {
                    if(Math.floor(index / 2) === winLevel) {
                        fire.playLoop();
                        fire.visible = true;
                    } else {
                        fire.stopLoop();
                        fire.visible = false;
                    }
                })
            },
            TweenLite.to(this._reelsHighlight, 0.2, {alpha : 1, ease : Power2.easeOut}),
            TweenLite.to(this._bigWinText, 0.2, {alpha : 1}),
            TweenLite.fromTo(this._bigWinText.scale, 0.3,
                {x : 0.5, y : 0.5},
                {
                    x : 1,
                    y : 1,
                    ease : Elastic.easeOut.config(2, 1),
                    onComplete : () => this._bigWinText.shake(0.1, 4)
                }),
            TweenLite.to(this._fireContainer, 0.2, {alpha : 1}),
            // it's a bit stupid to use the scale to keep the duration then pass the whole timeline duration to the count up. Maybe refactoring in the slot-game is needed.
            TweenLite.to(this, winRatioConfig.duration, {})
        ]);

        timeline.add(TweenLite.to(this._textGlow, 0.2, {alpha : 1}), 0);
        timeline.add(TweenLite.to(this._textGlow, 0.2, {alpha : 0.4}), winRatioConfig.duration - 0.2);

        return timeline;
    }

    private updateBigWinLevelAnimation(text:string):void {
        this._bigWinText.texture = GameResources.getTextures(`bigWin${text}`)[0];
    }

    public playCountUp(from:number, to:number, duration:number):Timeline | null {
        return this._countUp.getCountUpAnimation(from, to, duration);
    }

    public playEndingAnimation(winNumber:number, endWinRatioConfig:IWinRatioConfig, isAborted:boolean):void {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._bigWinText.stopShake();
                this.updateBigWinLevelAnimation(endWinRatioConfig.text!);
            },
            TweenLite.to(this._reelsHighlight, 0.5, {alpha : 0}),
            TweenLite.to(this._fireContainer, 0.5, {
                alpha : 0, onComplete : () => {
                    this._fires.forEach((fire:TimelineSprite) => {
                        fire.stopLoop();
                        fire.visible = false;
                    });
                }
            }),
            TweenLite.to(this._textGlow, 0.6, {alpha : 0}),
            TweenLite.fromTo(this._bigWinText, 0.3, {alpha : 1}, {alpha : 0, delay : 0.5}),
            () => EventHandler.dispatchEvent(new GameEvent(BackgroundAnimationsController.SET_FLASH_LIGHTS, false)),
            this._countUp.abort(winNumber, isAborted)
        ]);
        timeline.add(() => this._isShowing = false);
        timeline.play();
    }
}